import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminGet, adminPut } from './adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const GREEN = '#0B6B55';
const FIELDS = [['product_name', 'Product name'], ['batch_no', 'Batch number'], ['mrp', 'MRP'], ['expiry_date', 'Expiry date']];
const SOURCES = [['TYPED', 'Typed at dispatch'], ['CSV', 'CSV upload'], ['ERP', 'ERP export'], ['H2_SCAN', 'Scan H2 QR']];

export default function PassportContentPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    adminGet(`/customers/${tenantId}/passport-settings`).then(setS).catch(e => toast.error(e.message));
    adminGet(`/customers/${tenantId}`).then(setCustomer).catch(() => {});
  }, [tenantId]);

  const save = async () => {
    try {
      await adminPut(`/customers/${tenantId}/passport-settings`, s);
      toast.success('Passport content saved');
    } catch (e) { toast.error(e.message); }
  };

  if (!s) return <div className="p-8 text-sm" style={{ color: '#9a9e96' }}>Loading…</div>;
  const isProduct = s.certificate_type === 'PRODUCT_AND_LOGISTICS';
  const field = (key) => s.fields?.[key] || { collect: false, source: 'TYPED' };
  const setField = (key, patch) => setS({ ...s, fields: { ...s.fields, [key]: { ...field(key), ...patch } } });

  return (
    <div className="p-8" data-testid="passport-content-page">
      <button data-testid="back-to-journeys" onClick={() => navigate('/admin/journeys')}
        className="flex items-center gap-1.5 text-sm mb-3 hover:underline" style={{ color: '#6b7069' }}>
        <ArrowLeft size={15} /> Journey templates</button>
      <p className="text-sm" style={{ color: '#6b7069' }}>{customer?.legal_name || ''} · Passport settings</p>
      <h1 className="text-4xl font-bold mb-1" style={{ color: '#1B1D1C' }}>Passport content</h1>
      <p className="text-sm mb-6 max-w-lg" style={{ color: '#6b7069' }}>
        The customer decides which product data, if any, Track0tic receives. Without it, the passport still proves the cold chain.</p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div>
          <div className="rounded-lg border bg-white p-6 mb-6" style={{ borderColor: '#E5E3DC' }}>
            <h2 className="font-bold text-lg mb-4" style={{ color: '#1B1D1C' }}>Certificate type</h2>
            {[['PRODUCT_AND_LOGISTICS', 'Product + logistics', 'Names the product and batch; verifies both.'],
              ['LOGISTICS_ONLY', 'Logistics only', 'No product data held. Certifies only the conditions the shipment travelled in.']].map(([v, title, sub]) => (
              <button key={v} data-testid={`cert-type-${v}`} onClick={() => setS({ ...s, certificate_type: v })}
                className="w-full text-left rounded-lg border p-4 mb-3 transition-colors"
                style={s.certificate_type === v ? { background: '#E7F2EE', borderColor: GREEN } : { background: 'white', borderColor: '#D9D6CD' }}>
                <p className="font-bold text-sm" style={{ color: '#1B1D1C' }}>
                  <span className="inline-block w-3 h-3 rounded-full border-2 mr-2 align-middle"
                    style={{ borderColor: s.certificate_type === v ? GREEN : '#9a9e96', background: s.certificate_type === v ? GREEN : 'white' }} />
                  {title}</p>
                <p className="text-xs mt-1 ml-5" style={{ color: '#6b7069' }}>{sub}</p>
              </button>
            ))}
          </div>

          <div className="rounded-lg border bg-white p-6" style={{ borderColor: '#E5E3DC' }}>
            <h2 className="font-bold text-lg mb-4" style={{ color: '#1B1D1C' }}>Product fields (when product data is on)</h2>
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
                <th className="py-2 font-medium">Field</th><th className="font-medium">Collect</th><th className="font-medium">Source</th></tr></thead>
              <tbody>
                {FIELDS.map(([key, label]) => (
                  <tr key={key} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                    <td className="py-3">{label}</td>
                    <td className="pr-3">
                      <select data-testid={`collect-${key}`} value={field(key).collect ? 'on' : 'off'}
                        disabled={!isProduct}
                        onChange={e => setField(key, { collect: e.target.value === 'on' })}
                        className="h-9 px-2 rounded-md border bg-white text-sm w-32" style={{ borderColor: '#D9D6CD' }}>
                        <option value="off">Off</option><option value="on">On</option>
                      </select>
                    </td>
                    <td>
                      {field(key).collect && isProduct ? (
                        <select data-testid={`source-${key}`} value={field(key).source}
                          onChange={e => setField(key, { source: e.target.value })}
                          className="h-9 px-2 rounded-md border bg-white text-sm w-40" style={{ borderColor: '#D9D6CD' }}>
                          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : <span style={{ color: '#9a9e96' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
              Sources when on: typed at dispatch, CSV upload, or ERP export (e.g. Easysol). Each field can be switched on separately.</p>
            <Button data-testid="save-passport-content-btn" onClick={save} className="mt-4 text-white" style={{ background: GREEN }}>Save settings</Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className={`rounded-lg border p-5 ${!isProduct ? 'opacity-60' : ''}`}
            style={{ borderColor: isProduct ? GREEN : '#E5E3DC', borderWidth: isProduct ? 2 : 1, background: 'white' }}
            data-testid="preview-product">
            <p className="text-xs font-bold mb-3" style={{ color: isProduct ? GREEN : '#6b7069' }}>
              Preview · Product + logistics {isProduct && '(selected)'}</p>
            <p className="font-bold" style={{ color: '#1B1D1C' }}>Track0tic · Verified product</p>
            <div className="rounded-md border p-3 my-3" style={{ borderColor: '#EDEBE4' }}>
              <p className="font-bold text-sm">[Product name]</p>
              <p className="text-xs" style={{ color: '#6b7069' }}>Batch [no.] · MRP [₹] · Exp [date]</p>
              <p className="text-xs mt-2 px-2 py-1 rounded inline-block" style={{ background: '#E7F2EE', color: GREEN }}>Delivered within limits</p>
            </div>
            <p className="text-xs rounded-md border p-2" style={{ borderColor: '#EDEBE4', color: '#6b7069' }}>Last leg · [from] → you · [x]–[y] °C</p>
            <div className="mt-3 rounded-md py-2 text-center text-sm text-white" style={{ background: GREEN }}>Product certificate</div>
          </div>

          <div className={`rounded-lg border p-5 ${isProduct ? 'opacity-60' : ''}`}
            style={{ borderColor: !isProduct ? GREEN : '#E5E3DC', borderWidth: !isProduct ? 2 : 1, background: 'white' }}
            data-testid="preview-logistics">
            <p className="text-xs font-bold mb-3" style={{ color: !isProduct ? GREEN : '#6b7069' }}>
              Preview · Logistics only {!isProduct && '(selected)'}</p>
            <p className="font-bold" style={{ color: '#1B1D1C' }}>Track0tic · Cold-chain logistics</p>
            <div className="rounded-md border p-3 my-3" style={{ borderColor: '#EDEBE4' }}>
              <p className="font-bold text-sm">Shipment BK1-0001</p>
              <p className="text-xs" style={{ color: '#6b7069' }}>Shipped by {customer?.legal_name || '[Customer]'} · required 2–8 °C</p>
              <p className="text-xs mt-2 px-2 py-1 rounded inline-block" style={{ background: '#E7F2EE', color: GREEN }}>Travelled within 2–8 °C</p>
            </div>
            <p className="text-xs rounded-md border p-2" style={{ borderColor: '#EDEBE4', color: '#6b7069' }}>Last leg · [from] → you · [x]–[y] °C</p>
            <div className="mt-3 rounded-md py-2 text-center text-sm text-white" style={{ background: GREEN }}>Logistics certificate</div>
            <p className="text-xs mt-3" style={{ color: '#6b7069' }}>
              Certifies transport conditions for this labelled package only. It does not confirm product identity, batch or expiry.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
